import { useEffect, useMemo, useRef, useState } from "react";
import { endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import { Failed, Loading } from "./Loading.js";
import { Settings } from "./Settings.js";

/** Twice a minute is faster than the job moves; the number is never more than a beat stale. */
const ARTWORK_POLL_MS = 3000;

/**
 * Settings, wired.
 *
 * The consequence table needs four REAL artists, so it reads the artist rankings and takes the
 * ones with enough evidence to be interesting — a table of examples would demonstrate the
 * arithmetic without saying anything about this library, which is the opposite of the point.
 */
export function SettingsScreen() {
  const genres = useResource(() => endpoints.genres(), []);
  const tags = useResource(() => endpoints.tags(), []);
  const settings = useResource(() => endpoints.settings(), []);
  const artists = useResource(() => endpoints.rankingArtists(), []);
  const lastExport = useResource(() => endpoints.lastExport(), []);
  const artwork = useResource(() => endpoints.artworkProgress(), []);

  /**
   * `M` is held locally and written behind a debounce.
   *
   * The consequence table is the whole argument for the slider — four real artists' scores moving
   * as it moves — so the number has to change on the same frame as the thumb. Reading it back from
   * the server would make the table lag a drag by a round trip, and writing on every input event
   * would be a hundred writes across one gesture.
   */
  const [m, setM] = useState<number | null>(null);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shrinkageM = m ?? settings.data?.shrinkageM ?? 25;

  useEffect(() => () => { if (pending.current) clearTimeout(pending.current); }, []);

  /**
   * While the backfill runs, the progress line is re-read every few seconds.
   *
   * Polling rather than a stream: the job is 22–44 minutes long and moves twice a minute at
   * MusicBrainz's rate limit, so an SSE connection held open for half an hour would buy a
   * smoother number and nothing else. The interval exists only while `running` is true, so an
   * idle Settings screen makes no requests at all — and it starts on its own if you arrive here
   * with a run already in flight, which is what makes the job survivable across a reload.
   */
  const artworkRunning = artwork.data?.running ?? false;
  const reloadArtwork = artwork.reload;
  useEffect(() => {
    if (!artworkRunning) return;
    const timer = setInterval(reloadArtwork, ARTWORK_POLL_MS);
    return () => clearInterval(timer);
  }, [artworkRunning, reloadArtwork]);

  /**
   * Four artists spanning the range the slider actually changes: the smallest samples move most,
   * so a table of only well-covered artists would appear not to respond at all.
   */
  const consequence = useMemo(() => {
    const rated = (artists.data?.artists ?? []).filter((a) => a.ratedCount > 0 && a.raw !== null);
    const bySample = [...rated].sort((a, b) => a.ratedCount - b.ratedCount);
    const picks = [bySample[0], bySample[Math.floor(bySample.length / 3)], bySample[Math.floor((bySample.length * 2) / 3)], bySample.at(-1)];
    const seen = new Set<number>();
    return picks
      .filter((a): a is NonNullable<typeof a> => a !== undefined)
      .filter((a) => !seen.has(a.id) && seen.add(a.id) !== undefined)
      .map((a) => ({ id: a.id, name: a.name, ratedCount: a.ratedCount, raw: a.raw }));
  }, [artists.data]);

  const error = genres.error ?? tags.error ?? settings.error ?? artists.error;
  if (error) {
    return (
      <Failed
        error={error}
        retry={() => {
          genres.reload();
          tags.reload();
          settings.reload();
          artists.reload();
        }}
      />
    );
  }
  if (genres.loading || tags.loading || settings.loading || artists.loading) return <Loading what="settings" />;

  return (
    <Settings
      genres={genres.data?.genres ?? []}
      onAddGenre={(name) => void endpoints.addGenre(name).then(() => genres.reload())}
      onRemoveGenre={(id) =>
        void endpoints.removeGenre(id).then(() => {
          genres.reload();
          // Removing a genre returns its tags to the queue, so the queue has to be re-read.
          tags.reload();
        })
      }
      tags={tags.data?.tags ?? []}
      occurrences={tags.data?.occurrences ?? { decided: 0, total: 0, waiting: 0 }}
      onBulk={(selected, genreId, ignore) =>
        void endpoints.bulkTags(selected, genreId, ignore).then(() => {
          tags.reload();
          // Filing songs under a genre changes its count, which is on screen beside it.
          genres.reload();
        })
      }
      shrinkageM={shrinkageM}
      onShrinkageM={(value) => {
        setM(value);
        if (pending.current) clearTimeout(pending.current);
        pending.current = setTimeout(() => void endpoints.setSetting("shrinkage_m", value), 300);
      }}
      consequence={consequence}
      collection={artists.data?.collection ?? { totalRated: 0, mean: null }}
      artwork={artwork.data}
      onFetchArt={() =>
        void endpoints
          .startArtwork()
          // A 409 is another tab having started it a second earlier, and a 503 is a deploy with
          // no bucket — the answer to both is the same, which is to re-read what is actually
          // true rather than to state something on the strength of a request that failed.
          .catch(() => undefined)
          .then(() => artwork.reload())
      }
      onStopArt={() => void endpoints.stopArtwork().then(() => artwork.reload())}
      onRetryArt={() =>
        // Re-queued, not re-fetched: this moves rows back into the backlog and the count on screen
        // says so. Starting the run is still a decision somebody makes.
        void endpoints.retryArtwork().then(() => artwork.reload())
      }
      lastExport={lastExport.data?.lastExportAt ?? null}
      onExportCsv={() => globalThis.open("/api/export/ratings.csv", "_blank")}
      onExportJson={() => globalThis.open("/api/export/everything.json", "_blank")}
      onChangePassword={() => globalThis.alert("Change your password from the login screen's ⋯ menu.")}
    />
  );
}
